<template>
    <div class="datepicker-outer-container" :class="[{noMargin, noTodayButton: !canShowTodayButton}]" ref="root">
        <label>
            <div class="datepicker-container">
                <CqDateSelectInput
                    class="datepicker"
                    v-model="dateForInput"
                    :label="label"
                    :readonly="readonly"
                    :disabled="disabled"
                    :required="required"
                    :min="formattedMin"
                    :max="formattedMax"
                    :invalid="invalid"
                    :invalidFeedback="invalidFeedback"
                    style="height: 100%"
                    noMargin
                />
                <a data-clear v-if="!required" @click.stop>X</a>
            </div>
        </label>
        <CqButton
            v-if="canShowTodayButton"
            shape="flat"
            size="auto"
            margin="no-top-bottom"
            class="today-button"
            label="Vandaag"
            @click="changeToToday"
            :disabled="disabled || disableTodayButton"
        />
        <div ref="calendar" popover="manual" class="popover"></div>
    </div>
</template>

<script lang="ts">
    import {Component, Vue, Model, Prop, Ref} from 'vue-facing-decorator';
    import flatpickr from 'flatpickr';
    import Dates from '@cquential/date-utils';
    import {Dutch} from 'flatpickr/dist/l10n/nl';
    import {RosterData} from '@/types/types';
    import {CqButton, CqDateSelectInput} from '@cquential/ui';

    @Component({
        components: {
            CqButton,
            CqDateSelectInput,
        },
    })
    export default class CustomDateSelect extends Vue {
        @Model({type: Date}) public selectedDate?: Date;
        @Prop({type: Date}) public min?: Date;
        @Prop({type: Date}) public max?: Date;
        @Prop({type: String}) public label?: string;
        @Prop({type: Boolean}) public disabled!: boolean;
        @Prop({type: Boolean}) public disableTodayButton!: boolean;
        @Prop({type: Boolean}) public readonly!: boolean;
        @Prop({type: Boolean}) public required!: boolean;
        @Prop({type: Boolean}) public showRosterStatus!: boolean;
        @Prop({type: Boolean}) public hideTodayButton!: boolean;
        @Prop() public roster?: RosterData;
        @Prop({type: Boolean}) public invalid!: boolean;
        @Prop({type: String}) public invalidFeedback?: string;
        @Prop({type: Boolean}) public noMargin!: boolean;

        @Ref() public root!: HTMLElement;
        @Ref() public calendar!: HTMLDivElement;

        private flatpickrInstance: flatpickr.Instance | null = null;

        public mounted(): void {
            // flatpickr needs it to be shown for initialization
            this.calendar.showPopover();
            this.initialize();
        }

        public updated(): void {
            this.destroy();
            this.initialize();
        }

        public beforeUnmount(): void {
            this.destroy();
        }

        public get dateForInput(): Date | string | undefined {
            return this.selectedDate;
        }

        public set dateForInput(date: Date | string | undefined) {
            if (typeof date === 'string') {
                date = Dates.Utils.dateStringToAmsterdamMidnightDate(date);
            }

            this.selectedDate = date;
        }

        public get canShowTodayButton(): boolean {
            const today: Date = Dates.Utils.today();

            return !this.hideTodayButton && (!this.max || today <= this.max) && (!this.min || today >= this.min);
        }

        public get formattedMin(): string | undefined {
            return this.min ? Dates.Formatter.formatDate(this.min) : undefined;
        }

        public get formattedMax(): string | undefined {
            return this.max ? Dates.Formatter.formatDate(this.max) : undefined;
        }

        private destroy(): void {
            if (this.flatpickrInstance) {
                this.flatpickrInstance.destroy();
                this.flatpickrInstance = null;
            }
        }

        private getClassName(date: Date): string {
            if (!this.roster) {
                return '';
            }

            date = Dates.Utils.dateToAmsterdamMidnightDate(date);

            if (this.roster.endDate && this.roster.endDate <= date) {
                return 'ended';
            }

            if (this.roster.publishedUntil && this.roster.publishedUntil >= date) {
                return 'published';
            }

            if (this.roster.proposedUntil && this.roster.proposedUntil >= date) {
                return 'proposed';
            }

            return 'inConstruction';
        }

        private initialize(): void {
            const flatpickrOptions: flatpickr.Options.Options = {
                weekNumbers: true,
                defaultDate: this.selectedDate ? Dates.Formatter.formatDateComputerFriendly(this.selectedDate) : '',
                minDate: this.min ? Dates.Formatter.formatDateComputerFriendly(this.min) : '',
                maxDate: this.max ? Dates.Formatter.formatDateComputerFriendly(this.max) : '',
                locale: Dutch,
                altInput: true,
                altFormat: 'd-m-Y',
                appendTo: this.calendar,
                allowInput: !this.readonly,
                onOpen: this.flatpickrOpened,
                onClose: this.flatpickrClosed,
            };

            if (this.showRosterStatus) {
                flatpickrOptions.onDayCreate = (dObj: Date[], dStr: string, fp: flatpickr.Instance, dayElem: HTMLSpanElement & {dateObj: Date}): void => {
                    if (!this.roster) {
                        return;
                    }

                    const className: string = this.getClassName(dayElem.dateObj);
                    dayElem.classList.add(className);
                };
            }

            if (this.root) {
                const inputElement: HTMLInputElement = this.root.querySelector('.datepicker-container input')!;
                this.flatpickrInstance = flatpickr(inputElement, flatpickrOptions);

                // could be cleaner, but otherwise, when it's included in an openable menu, the menu will close when you click inside the flatpickr
                this.flatpickrInstance.calendarContainer.addEventListener('click', (e: MouseEvent): void => {
                    e.stopPropagation();
                });
                if (this.disabled) {
                    this.flatpickrInstance._input.setAttribute('disabled', 'disabled');
                }

                // copy data-v-xxx attribute (and for the sake of argument others too), so that the flatpickr input gets the same styling as the native input
                for (const key in inputElement.dataset) {
                    this.flatpickrInstance._input.dataset[key] = inputElement.dataset[key];
                }
            }
        }

        private flatpickrOpened(): void {
            // move popover to top of top layer (this is not a pun, this is an accurate description of the way I understand the top layer works)
            this.calendar.hidePopover();
            this.calendar.showPopover();
        }

        private flatpickrClosed(selectedDates: Date[]): void {
            if (selectedDates.length > 0) {
                const selectedTime: number = selectedDates[0].valueOf();
                const currentTime: number | undefined = this.selectedDate?.valueOf();

                if (selectedTime !== currentTime) {
                    this.dateForInput = selectedDates[0];
                }
            }
        }

        public changeToToday(): void {
            const date: Date = Dates.Utils.today();
            if (this.selectedDate && date.valueOf() === this.selectedDate.valueOf()) {
                // nothing changed
                return;
            }

            this.selectedDate = date;
        }
    }
</script>

<style lang="scss">
    @import '@/../node_modules/flatpickr/dist/flatpickr.css';
    @import '@/../node_modules/flatpickr/dist/themes/airbnb.css';

    .flatpickr-calendar {
        box-shadow: var(--generic-shadow-average);
        background: var(--calendar-background);

        .flatpickr-day {
            &.selected {
                background: var(--calendar-selected-background) !important;
                border-color: var(--calendar-selected-border);
            }

            &.today {
                border-bottom: 3px solid var(--calender-today-border) !important;
            }
        }

        .flatpickr-rContainer .flatpickr-day {
            &.published {
                box-shadow: inset 0 3px 0 0 var(--published-header-color);
            }

            &.proposed {
                box-shadow: inset 0 3px 0 0 var(--construction-header-color);
                background: var(--proposed-gradient-header-background) no-repeat;
            }

            &.inConstruction {
                box-shadow: inset 0 3px 0 0 var(--construction-header-color);
            }

            &.ended {
                box-shadow: inset 0 3px 0 0 var(--ended-header-color);
            }
        }

        .flatpickr-monthDropdown-months {
            background: var(--calendar-background);
        }

        &.arrowTop::before {
            border-bottom-color: var(--calendar-background);
        }

        &.arrowTop::after {
            border-bottom-color: var(--calendar-background);
        }
    }
</style>

<style lang="scss" scoped>
    div.datepicker-outer-container {
        border-radius: var(--theme-border-radius);
        color: var(--color);
        border: 1px solid var(--cq-medium-grey);
        background: var(--theme-light);
        height: 3.5rem;
        display: grid;
        grid-template-columns: 1fr max-content;

        &.noTodayButton {
            display: block;
        }

        &:not(.noMargin) {
            margin-bottom: 1rem;
        }

        &:has(input:valid) {
            border-color: var(--cq-green);
        }

        label {
            display: flex;
            place-content: center space-between;
            align-items: center;
            height: 100%;
            max-height: var(--optionsrow-height);
            box-sizing: border-box;
            overflow: hidden;

            span {
                color: var(--label-color);
                flex: 0 1 auto;
                padding: 0 1em;
                min-width: 4rem;

                ~ div.datepicker-container {
                    :deep(input) {
                        padding: 0 1em;
                        flex: 1;
                        font-weight: bold;
                        color: var(--input-color);
                        background: var(--select-input-background);
                        height: 100%;
                        min-height: var(--optionsrow-height);
                    }
                }
            }
        }

        div.datepicker-container {
            width: 100%;
            height: 100%;

            :deep(input) {
                border: 0;
                appearance: none;
                display: inline-block;
                width: 100%;
                height: 100%;
                border-radius: 3px;
                color: var(--color);
                font-family: Roboto, -apple-system, 'Helvetica Neue', Helvetica, Arial, sans-serif;
                font-size: 0.9rem;
                padding: 0 1rem;

                // arrow
                background: transparent;

                &:not([required]) {
                    width: calc(100% - 20px);
                }
            }
        }

        .today-button {
            border-top-left-radius: 0;
            border-bottom-left-radius: 0;
            margin: 0;
            line-height: 1.4rem;
            box-shadow: none;
            font-weight: bold;
        }

        &.field-value-block select {
            padding: 4px 1px 6px 0; // padding right of 0 causes the element to be too wide (in firefox) for some retarded reason
            border-radius: 0;
            border-bottom: 1px solid #888;

            &:focus {
                padding-bottom: 5px;
                border-bottom: 2px solid #54a5b0;
            }
        }
    }

    a[data-clear] {
        cursor: pointer;
    }

    .popover {
        width: 0;
        height: 0;
        margin: 0;
        border: 0;
        padding: 0;
        overflow: visible;
    }
</style>
